import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Helmet } from 'react-helmet-async';
import { TOOLS, CATEGORIES } from '../constants';
import { Link } from 'react-router-dom';
import { ArrowUpRight, Search, Star, X, MessageSquare, Send, ShieldCheck, Mail, Filter } from 'lucide-react';
import { cn } from '../lib/utils';
import { suggestionsService, auth, newsletterService } from '../lib/firebase';
import { onAuthStateChanged } from 'firebase/auth';

export default function Dashboard({ favorites, toggleFavorite, setToast }: { favorites: string[], toggleFavorite: (id: string) => void, setToast?: (toast: any) => void }) {
  const [search, setSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [isSuggestModalOpen, setIsSuggestModalOpen] = useState(false);
  const [suggestData, setSuggestData] = useState({ name: '', description: '' });
  const [suggestionCount, setSuggestionCount] = useState(0);
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    try {
      const unsubscribe = suggestionsService.subscribeToSuggestions((suggestions) => {
        setSuggestionCount(suggestions.length);
      });
      return () => unsubscribe();
    } catch (error) {
      console.warn('Dashboard stats listener failed:', error);
    }
  }, []);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setIsAdmin(user?.email === 'allysonbelo@gmail.com');
    });
    return () => unsubscribe();
  }, []);

  const handleSuggestSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!suggestData.name || !suggestData.description) return;
    
    try {
      await suggestionsService.addSuggestion(suggestData.name, suggestData.description);
      
      setIsSuggestModalOpen(false);
      setSuggestData({ name: '', description: '' });
      
      if (setToast) {
        setToast({ message: "Suggestion saved! Thanks for helping us grow.", type: 'success' });
      }
    } catch (error) {
      console.error('Firebase Error:', error);
      if (setToast) {
        setToast({ message: "Database error. Please try again later.", type: 'info' });
      }
    }
  };

  const filteredTools = TOOLS.filter(t => {
    const matchesSearch = t.name.toLowerCase().includes(search.toLowerCase()) || t.description.toLowerCase().includes(search.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || t.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const favoriteTools = TOOLS.filter(t => favorites.includes(t.id));

  const container = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: { staggerChildren: 0.05 }
    }
  };

  const item = {
    hidden: { scale: 0.95, opacity: 0 },
    show: { scale: 1, opacity: 1 }
  };

  return (
    <motion.div 
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="p-6 md:p-10 max-w-[1400px] mx-auto w-full"
    >
      <Helmet>
        <title>Dashboard - QuicklyUtils | Free & Fast Web Tools</title>
        <meta name="description" content="Access all your free utility tools in one place. AI rewriting, text conversion, SEO utilities, and more. 100% free and fast." />
        <link rel="canonical" href="https://quicklyutils.com/" />
        <script type="application/ld+json">
          {`
            {
              "@context": "https://schema.org",
              "@type": "WebApplication",
              "name": "QuicklyUtils",
              "url": "https://quicklyutils.com/",
              "description": "The ultimate toolbox for developers and writers. AI-powered rewriting, text conversion, and more.",
              "applicationCategory": "Utility",
              "operatingSystem": "All",
              "offers": {
                "@type": "Offer",
                "price": "0",
                "priceCurrency": "USD"
              }
            }
          `}
        </script>
      </Helmet>

      <header className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-6">
        <div>
          <h1 className="text-3xl font-bold tracking-tight mb-2 dark:text-white">Free Online Web Tools</h1>
          <p className="text-slate-500 dark:text-slate-400 text-sm max-w-xl">Welcome to QuicklyUtils, your complete toolbox for developers, writers, and marketers. Access dozens of fast utilities—AI-powered features, code formatters, and SEO tools—all 100% free directly in your browser.</p>
        </div>
        <div className="relative w-full md:w-96">
          <div className="absolute inset-y-0 left-4 flex items-center pointer-events-none">
            <Search className="w-4 h-4 text-slate-400" />
          </div>
          <input 
            type="text" 
            placeholder="Search for a tool (e.g. JSON, AI)..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full bg-white dark:bg-slate-900 border border-transparent dark:border-slate-800 rounded-full py-3 pl-12 pr-4 text-sm shadow-sm sm:shadow-[0_8px_30px_rgb(0,0,0,0.04)] dark:shadow-none outline-none focus:ring-2 focus:ring-accent/10 focus:border-accent transition-all dark:text-white"
          />
        </div>
      </header>

      {/* Favorites Section */}
      {favoriteTools.length > 0 && !search && (
        <section className="mb-12">
          <div className="flex items-center gap-2 mb-6">
            <Star className="w-4 h-4 text-accent fill-accent" />
            <h2 className="text-sm font-bold uppercase tracking-widest text-slate-400">Your Favorites</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 lg:grid-cols-6 gap-4">
            {favoriteTools.map(tool => (
              <div key={`dash-fav-${tool.id}`} className="relative group">
                <Link 
                  to={tool.path}
                  className="bento-card p-4 flex flex-col items-center text-center group hover:border-accent transition-all animate-in fade-in zoom-in duration-300 h-full"
                >
                  <div className="w-12 h-12 bg-slate-50 dark:bg-slate-800 rounded-2xl flex items-center justify-center text-slate-400 group-hover:bg-accent/10 group-hover:text-accent transition-all mb-3">
                    <tool.icon className="w-6 h-6" />
                  </div>
                  <h3 className="text-xs font-bold leading-tight line-clamp-1">{tool.name}</h3>
                </Link>
                <button
                  aria-label={`Remove ${tool.name} from favorites`}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    toggleFavorite(tool.id);
                  }}
                  className="absolute -top-2 -right-2 w-6 h-6 bg-rose-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all hover:scale-110 shadow-lg z-10"
                  title="Remove from favorites"
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Categories Filter */}
      {!search && (
        <section className="mb-8">
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex items-center gap-2 text-slate-500 dark:text-slate-400 mr-2">
              <Filter className="w-4 h-4" />
              <span className="text-xs font-bold uppercase tracking-widest">Filter by</span>
            </div>
            <button
              onClick={() => setSelectedCategory('all')}
              className={cn(
                "px-4 py-2 rounded-full text-sm font-medium transition-all",
                selectedCategory === 'all' 
                  ? "bg-slate-800 dark:bg-white text-white dark:text-slate-900 shadow-md scale-105" 
                  : "bg-white dark:bg-slate-900 text-slate-600 dark:text-slate-400 border border-transparent dark:border-slate-800 shadow-sm hover:shadow-md dark:shadow-none hover:text-slate-900 dark:hover:text-white"
              )}
            >
              All Tools
            </button>
            {CATEGORIES.map(category => (
              <button
                key={category.id}
                onClick={() => setSelectedCategory(category.id)}
                className={cn(
                  "px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 group flex items-center gap-2",
                  selectedCategory === category.id 
                    ? "bg-slate-800 dark:bg-white text-white dark:text-slate-900 shadow-md scale-105" 
                    : "bg-white dark:bg-slate-900 text-slate-600 dark:text-slate-400 border border-transparent dark:border-slate-800 shadow-sm hover:shadow-md dark:shadow-none hover:text-slate-900 dark:hover:text-white"
                )}
              >
                <span>{category.name}</span>
              </button>
            ))}
          </div>
        </section>
      )}

      <motion.div 
        variants={container}
        initial="hidden"
        animate="show"
        className="grid grid-cols-1 md:grid-cols-12 gap-4 sm:gap-6 auto-rows-[minmax(180px,auto)]"
      >
        {/* Featured Card - AI Smart Rewriter */}
        {!search && (
          <motion.div variants={item} className="col-span-1 md:col-span-12 lg:col-span-6 md:row-span-2 bento-card-indigo group p-6 md:p-8 flex flex-col justify-between overflow-hidden">
            <div className="relative z-10">
              <span className="bg-black/20 text-white border border-white/10 text-[10px] font-black uppercase tracking-widest px-3 py-1 rounded-full">Top AI Tool</span>
              <h2 className="text-3xl md:text-4xl font-bold mt-6 leading-tight">AI Smart<br className="hidden sm:block" /> Text Rewriter</h2>
              <p className="text-indigo-100 mt-4 max-w-xs opacity-90 text-sm">Perfect your tone, clarity, and impact with our advanced language model.</p>
            </div>
            <Link to="/tools/ai-rewriter" className="relative z-10 mt-6 bg-white text-accent w-fit px-8 py-3 rounded-xl font-bold hover:bg-slate-50 transition-colors flex items-center gap-2">
              Try Now <ArrowUpRight className="w-4 h-4" />
            </Link>
            <div className="absolute -right-8 -bottom-8 w-64 h-64 bg-white/10 rounded-full blur-3xl group-hover:bg-white/20 transition-colors"></div>
            <div className="absolute right-10 top-10 w-24 h-24 border-4 border-white/10 rounded-full group-hover:scale-110 transition-transform hidden md:block"></div>
          </motion.div>
        )}

        {/* Regular Tools in Bento Grid */}
        {filteredTools.map((tool, idx) => {
          const isLarge = !search && (idx === 0 || idx === 1);
          const colSpan = search ? "col-span-1 md:col-span-6 lg:col-span-4" : (isLarge ? "col-span-1 md:col-span-6 lg:col-span-3" : "col-span-1 md:col-span-6 lg:col-span-4");
          const rowSpan = isLarge ? "md:row-span-2" : "md:row-span-1";
          const isFavorited = favorites.includes(tool.id);

          return (
            <motion.div key={tool.id} variants={item} className={cn(colSpan, rowSpan, "relative group")}>
              <button 
                aria-label={isFavorited ? `Remove ${tool.name} from favorites` : `Add ${tool.name} to favorites`}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  toggleFavorite(tool.id);
                }}
                className={cn(
                  "absolute top-4 right-4 z-20 p-2 rounded-xl transition-all hover:scale-110 active:scale-95",
                  isFavorited ? "text-accent" : "text-slate-300 opacity-0 group-hover:opacity-100"
                )}
              >
                <Star className={cn("w-4 h-4", isFavorited ? "fill-accent" : "")} />
              </button>
              
              <Link 
                to={tool.path}
                className="bento-card group p-6 flex flex-col h-full hover:border-accent hover:-translate-y-1 hover:shadow-xl transition-all duration-300 relative overflow-hidden"
              >
                <div className="flex justify-between items-start mb-4">
                  <div className={cn(
                    "w-12 h-12 rounded-2xl flex items-center justify-center transition-colors",
                    idx % 3 === 0 ? "bg-amber-50 dark:bg-amber-900/10 text-amber-600" : 
                    idx % 3 === 1 ? "bg-emerald-50 dark:bg-emerald-900/10 text-emerald-600" : "bg-rose-50 dark:bg-rose-900/10 text-rose-600"
                  )}>
                    <tool.icon className="w-6 h-6" />
                  </div>
                </div>
                
                <h3 className="font-bold text-lg mb-2 group-hover:text-accent transition-colors">
                  {tool.name}
                </h3>
                <p className="text-slate-600 dark:text-slate-400 text-sm line-clamp-3 mb-4">
                  {tool.description}
                </p>
                
                <div className="mt-auto flex items-end justify-between">
                   <span className="text-[10px] font-bold text-accent bg-accent/10 px-2 py-1 rounded-md uppercase tracking-widest whitespace-nowrap inline-block">
                     {CATEGORIES.find(c => c.id === tool.category)?.name || tool.category}
                   </span>
                </div>
              </Link>
            </motion.div>
          );
        })}

        {/* Suggest Tool Card */}
        {!search && (
          <motion.div 
            variants={item} 
            onClick={() => setIsSuggestModalOpen(true)}
            className="md:col-span-3 md:row-span-1 bento-card-dashed p-6 flex flex-col items-center justify-center text-center group cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800/10"
          >
            <div className="text-slate-300 dark:text-slate-700 text-3xl mb-1 group-hover:text-accent transition-colors font-bold">+</div>
            <h4 className="font-bold text-slate-700 dark:text-slate-300 text-sm">Suggest a Tool</h4>
            <p className="text-slate-500 dark:text-slate-400 text-[10px] mt-1 font-medium">What should we build next?</p>
          </motion.div>
        )}

        {/* Stats Card */}
        {!search && (
          <motion.div variants={item} className="md:col-span-3 md:row-span-1 bento-card-dark p-6 flex flex-col justify-between group overflow-hidden relative">
             <div className="relative z-10">
               <h4 className="text-slate-400 text-[10px] font-bold uppercase tracking-widest mb-1">Community Insights</h4>
               <p className="text-2xl font-bold text-white">{suggestionCount > 0 ? `+${suggestionCount}` : '...'}</p>
             </div>
             <div className="relative z-10">
               <p className="text-[10px] text-slate-300 font-medium leading-tight">Tools suggested by the community via your feedback.</p>
             </div>
             <MessageSquare className="absolute -right-4 -bottom-4 w-24 h-24 text-white/10 transition-transform group-hover:scale-110 rotate-[-12deg] select-none pointer-events-none" />
          </motion.div>
        )}
      </motion.div>

      {/* Newsletter Section */}
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        className="mt-12 mb-12 p-8 md:p-12 bento-card relative overflow-hidden group"
      >
        <div className="relative z-10 max-w-2xl">
          <h3 className="text-2xl md:text-3xl font-bold mb-4 text-slate-900 dark:text-white">Stay in the loop.</h3>
          <p className="text-slate-500 dark:text-slate-400 text-sm md:text-base mb-8 max-w-md">
            Get notified immediately whenever we launch a new high-performance utility. No spam, just tools.
          </p>
          
          <NewsletterForm setToast={setToast} />
          
          <p className="text-[10px] text-slate-500 mt-6 uppercase tracking-widest font-bold">
            By subscribing, you agree to our <Link to="/privacy" className="text-accent underline">Privacy Policy</Link>.
          </p>
        </div>
        
        <div className="absolute right-0 top-0 w-1/3 h-full bg-accent/10 blur-[100px] -mr-20"></div>
        <Mail className="absolute -right-12 -bottom-12 w-64 h-64 text-slate-900/5 dark:text-white/5 rotate-[-12deg] group-hover:scale-110 transition-transform" />
      </motion.div>

      {/* Suggest Tool Modal */}
      <AnimatePresence>
        {isSuggestModalOpen && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsSuggestModalOpen(false)}
              className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm"
            />
            <motion.div 
              initial={{ scale: 0.9, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 20 }}
              className="relative w-full max-w-lg bg-white dark:bg-slate-900 rounded-[2.5rem] p-10 shadow-2xl border border-line dark:border-slate-800"
            >
              <div className="flex items-center justify-between mb-8">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-accent/10 text-accent rounded-2xl flex items-center justify-center">
                    <MessageSquare className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold dark:text-white">Suggest a Tool</h3>
                    <p className="text-slate-500 dark:text-slate-400 text-xs">Help us expand our utility library.</p>
                  </div>
                </div>
                <button 
                  aria-label="Close"
                  onClick={() => setIsSuggestModalOpen(false)}
                  className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-xl transition-colors"
                >
                  <X className="w-5 h-5 dark:text-white" />
                </button>
              </div>

              <form onSubmit={handleSuggestSubmit} className="space-y-6">
                <div>
                  <label className="block text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-2 ml-1">Tool Name</label>
                  <input 
                    type="text" 
                    required
                    placeholder="e.g. SVG Optimizer, Image Resizer..."
                    value={suggestData.name}
                    onChange={(e) => setSuggestData({ ...suggestData, name: e.target.value })}
                    className="w-full bg-slate-50 dark:bg-slate-800/50 border border-line dark:border-slate-800 rounded-2xl py-4 px-6 text-sm outline-none focus:ring-2 focus:ring-accent/10 focus:border-accent transition-all dark:text-white"
                  />
                </div>

                <div>
                  <label className="block text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-2 ml-1">Description / Functionality</label>
                  <textarea 
                    required
                    rows={4}
                    placeholder="How should this tool work? What problems does it solve?"
                    value={suggestData.description}
                    onChange={(e) => setSuggestData({ ...suggestData, description: e.target.value })}
                    className="w-full bg-slate-50 dark:bg-slate-800/50 border border-line dark:border-slate-800 rounded-2xl py-4 px-6 text-sm outline-none focus:ring-2 focus:ring-accent/10 focus:border-accent transition-all dark:text-white resize-none"
                  />
                </div>

                <button 
                  type="submit"
                  className="w-full py-4 bg-accent text-white rounded-2xl font-bold text-sm hover:bg-accent/90 transition-all shadow-lg shadow-accent/20 flex items-center justify-center gap-2 group"
                >
                  <Send className="w-4 h-4 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
                  Submit Suggestion
                </button>

                <p className="text-[9px] text-center text-slate-400">
                  Our engineering team reviews all suggestions weekly. Thank you for your contribution!
                </p>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

function NewsletterForm({ setToast }: { setToast: (toast: any) => void }) {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await newsletterService.subscribe(email);
      setToast({ message: 'Successfully subscribed to newsletter!', type: 'success' });
      setEmail('');
    } catch (err) {
      setToast({ message: 'Error subscribing. Please try again.', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-3 max-w-lg">
      <input
        type="email"
        placeholder="your@email.com"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        required
        className="flex-1 bg-slate-100 dark:bg-white/10 border border-slate-200 dark:border-white/20 rounded-2xl px-6 py-4 text-sm focus:outline-none focus:border-accent dark:focus:border-accent transition-colors text-slate-900 dark:text-white placeholder:text-slate-500"
      />
      <button
        type="submit"
        disabled={loading}
        className="bg-accent hover:bg-accent/90 text-white font-bold px-8 py-4 rounded-2xl text-sm transition-all shadow-xl shadow-accent/20 disabled:opacity-50"
      >
        {loading ? 'Subscribing...' : 'Notify Me'}
      </button>
    </form>
  );
}
